/**
 * Arlo — Free gift with purchase (replaces Unicorn Cart's threshold gift)
 * 2026-10-15
 *
 * Config comes from snippets/arlo-free-gift.liquid (generated by Arlo Admin →
 * Theme Settings → Cart → "Free gift with purchase"), which is rendered on
 * every page from layout/theme.liquid and sets window.ARLO_FREE_GIFT.
 *
 * What it does, on every cart change:
 *   - Spend so far = sum of line prices (after discounts), not counting the
 *     gift line itself or Parcel Protection.
 *   - Draws a progress bar + gift card at the top of the cart drawer.
 *   - Below the threshold: "Add $X to get a FREE gift". Gift removed if it's
 *     in the cart.
 *   - At/over the threshold: "Claim my free gift" button (or adds it
 *     automatically when the admin's auto-add is on — unless the customer has
 *     removed it themselves this visit).
 *   - Outside the schedule (start/end) or switched off: no bar, and any gift
 *     we added is taken out of the cart.
 *   - Gift quantity is kept at 1.
 *
 * Why script, not Liquid: when the cart changes, the theme's refreshQuickCart()
 * only re-renders the item rows, heading, subtotal, discounts and cross-sells —
 * anything else at the top of the drawer stays as it was at page load. So this
 * re-reads /cart.js and redraws itself after every change.
 *
 * The $0 price itself is NOT done here: a Shopify automatic discount makes the
 * gift free (see ARCHITECTURE.md "Free gift with purchase"). This file only
 * adds/removes the gift line, tagged with the hidden property _arlo_free_gift.
 */
(function () {
  'use strict';
  // Arlo Admin's cart preview loads this same file with ARLO_FREE_GIFT_PREVIEW
  // set: it only gets window.ArloFreeGiftView (the drawing code), no cart logic,
  // so the preview always looks exactly like the storefront.
  var PREVIEW = !!window.ARLO_FREE_GIFT_PREVIEW;
  if (!PREVIEW) {
    if (window.__arloFreeGift) return;
    window.__arloFreeGift = true;
  }

  var C = window.ARLO_FREE_GIFT;
  if (!PREVIEW && (!C || !C.enabled || !C.product || !C.product.variants || !C.product.variants.length)) return;

  var PROT_VARIANT_ID = 43506344755382; // Parcel Protection — never counts towards the spend
  var GIFT_PROP = '_arlo_free_gift';
  var DECLINED_KEY = 'arloGiftDeclined';
  var giftVariantIds = C && C.product ? C.product.variants.map(function (v) { return Number(v.id); }) : [];

  // ── helpers ────────────────────────────────────────────────────────────────
  function now() { return Date.now(); }
  function inSchedule() {
    if (C.startMs && now() < C.startMs) return false;
    if (C.endMs && now() >= C.endMs) return false;
    return true;
  }
  function availableVariants() {
    return C.product.variants.filter(function (v) { return v.available; });
  }
  function active() { return inSchedule() && availableVariants().length > 0; }

  function money(cents, short) {
    var d = cents / 100;
    if (short && cents % 100 === 0) return '$' + d.toFixed(0);
    return '$' + d.toFixed(2);
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  // Admin text → HTML: {remaining} {threshold} {value} placeholders, plus
  // **bold**, *italic*, __underline__ (\* for a literal asterisk).
  function fmt(text, vars) {
    var s = esc(text || '').replace(/\\\*/g, '\u0001');
    s = s.replace(/\{(remaining|threshold|value)\}/g, function (_, k) { return vars[k] || ''; });
    s = s.replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>')
         .replace(/__([\s\S]+?)__/g, '<u>$1</u>')
         .replace(/\*([\s\S]+?)\*/g, '<em>$1</em>');
    return s.replace(/\u0001/g, '*');
  }
  function declined() { try { return sessionStorage.getItem(DECLINED_KEY) === '1'; } catch (e) { return false; } }
  function setDeclined(on) {
    try { on ? sessionStorage.setItem(DECLINED_KEY, '1') : sessionStorage.removeItem(DECLINED_KEY); } catch (e) {}
  }

  function isGiftLine(item) {
    return giftVariantIds.indexOf(Number(item.variant_id)) !== -1 &&
           item.properties && item.properties[GIFT_PROP] != null;
  }
  function spend(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      if (isGiftLine(item) || Number(item.variant_id) === PROT_VARIANT_ID) return sum;
      var line = item.final_line_price != null ? item.final_line_price : item.line_price;
      return sum + (line || 0);
    }, 0);
  }
  function giftValueCents(variant) {
    var v = variant || availableVariants()[0] || C.product.variants[0];
    return Math.max(v.price || 0, v.compare_at_price || 0);
  }

  function emitCartChanged() {
    if (window.emit) {
      window.emit('quick-cart:updated');
      window.emit('cart:updated');
    }
    // Cart page has no drawer refresh — reload it so lines and totals match.
    if (/^\/cart\/?$/.test(location.pathname)) location.reload();
  }
  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error(url + ' HTTP ' + r.status); return r.json(); });
  }

  // ── cart state ─────────────────────────────────────────────────────────────
  var busy = false;          // an add/remove of ours is in flight
  var weChanged = false;     // the last gift change was ours (not the customer's)
  var lastHadGift = null;    // was the gift in the cart last time we looked?
  var lastCart = null;

  function addGift(variantId) {
    if (busy) return Promise.resolve();
    busy = true; weChanged = true;
    var props = {}; props[GIFT_PROP] = 'Free gift';
    return post('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1, properties: props }] })
      .then(function () { setDeclined(false); })
      .catch(function (e) { console.warn('[ArloGift] add failed', e); })
      .then(function () { busy = false; emitCartChanged(); refresh(); });
  }
  function setGiftQty(items, qty) {
    if (busy || !items.length) return Promise.resolve();
    busy = true; weChanged = true;
    var updates = {};
    items.forEach(function (it, i) { updates[it.key] = i === 0 ? qty : 0; });
    return post('/cart/update.js', { updates: updates })
      .catch(function (e) { console.warn('[ArloGift] update failed', e); })
      .then(function () { busy = false; emitCartChanged(); refresh(); });
  }

  // Decide what the cart should look like, fix it if needed, then draw.
  function evaluate(cart) {
    lastCart = cart;
    var giftLines = (cart.items || []).filter(isGiftLine);
    var hasGift = giftLines.length > 0;
    var total = spend(cart);
    var on = active();
    var eligible = on && total >= C.thresholdCents;

    // Customer took the gift out themselves while still eligible → don't
    // auto-add it again this visit (they can still press Claim).
    if (lastHadGift && !hasGift && !weChanged && !busy && eligible) setDeclined(true);
    if (!busy) weChanged = false;
    lastHadGift = hasGift;

    if (!busy) {
      if (hasGift && !eligible) { setGiftQty(giftLines, 0); }
      else if (hasGift && (giftLines.length > 1 || giftLines[0].quantity > 1)) { setGiftQty(giftLines, 1); }
      else if (!hasGift && eligible && C.autoAdd && !declined()) {
        var v = availableVariants();
        if (v.length === 1) addGift(v[0].id);
      }
    }
    draw(cart, { on: on, total: total, hasGift: hasGift, eligible: eligible });
  }

  // ── drawing ────────────────────────────────────────────────────────────────
  var GIFT_SVG = '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>';

  function cssText() {
    var k = C.colours || {};
    return '' +
      '.arlo-gift{padding:8px 16px 2px;color:' + (k.text || '#111') + ';}' +
      '.arlo-gift__head{text-align:center;font-size:13.5px;line-height:1.3;margin:0 0 5px;}' +
      '.arlo-gift__head strong{font-weight:700;}' +
      '.arlo-gift__bar{position:relative;display:flex;align-items:center;margin:0 0 2px;}' +
      '.arlo-gift__track{flex:1;height:7px;border-radius:7px;background:' + (k.barTrack || '#e6e6e6') + ';overflow:hidden;margin-right:-6px;}' +
      '.arlo-gift__fill{display:block;height:100%;border-radius:7px;background:' + (k.barFill || '#111') + ';transition:width .4s ease;}' +
      '.arlo-gift__goal{position:relative;z-index:1;flex:0 0 auto;width:26px;height:26px;border-radius:50%;background:#fff;border:1.5px solid ' + (k.barFill || '#111') + ';display:flex;flex-direction:column;align-items:center;justify-content:center;font-size:7px;line-height:1;letter-spacing:-.02em;color:' + (k.text || '#111') + ';}' +
      '.arlo-gift--met .arlo-gift__goal{background:' + (k.barFill || '#111') + ';color:#fff;}' +
      '.arlo-gift__goal-label{text-align:right;font-size:10.5px;line-height:1.2;font-weight:700;margin:1px 0 5px;}' +
      '.arlo-gift__card{display:flex;align-items:stretch;border:1.5px solid ' + (k.cardBorder || '#555') + ';border-radius:6px;overflow:hidden;background:' + (k.cardBg || '#F7B597') + ';color:' + (k.cardText || '#111') + ';margin:0 0 6px;}' +
      '.arlo-gift__img{flex:0 0 52px;background:#fff;display:flex;align-items:center;justify-content:center;}' +
      '.arlo-gift__img img{width:52px;height:100%;min-height:52px;object-fit:cover;display:block;}' +
      '.arlo-gift__body{flex:1;min-width:0;padding:5px 9px;font-size:12.5px;line-height:1.3;}' +
      '.arlo-gift__actions{display:flex;flex-wrap:wrap;gap:6px;margin-top:5px;}' +
      '.arlo-gift__select{flex:1 1 120px;min-width:0;font-size:12.5px;padding:4px 6px;border:1px solid rgba(0,0,0,.35);border-radius:4px;background:#fff;color:#111;}' +
      '.arlo-gift__btn{flex:0 0 auto;font:inherit;font-size:12.5px;font-weight:700;padding:5px 12px;border-radius:4px;border:0;cursor:pointer;background:' + (k.buttonBg || '#111') + ';color:' + (k.buttonText || '#fff') + ';}' +
      '.arlo-gift__btn[disabled]{opacity:.6;cursor:default;}';
  }
  function injectStyles() {
    if (document.getElementById('arlo-gift-styles')) return;
    var st = document.createElement('style');
    st.id = 'arlo-gift-styles';
    st.textContent = cssText();
    document.head.appendChild(st);
  }

  function blockHtml(cart, s) {
    var remaining = Math.max(0, C.thresholdCents - s.total);
    var pct = Math.max(0, Math.min(100, Math.round(100 * s.total / C.thresholdCents)));
    var vars = { remaining: money(remaining), threshold: money(C.thresholdCents, true), value: money(giftValueCents(), true) };
    var t = C.text || {};
    var head, cardText, actions = '';
    if (!s.eligible) {
      head = fmt(t.bar, vars);
      cardText = fmt(t.card, vars);
    } else if (s.hasGift) {
      head = fmt(t.barDone, vars);
      cardText = fmt(t.cardAdded, vars);
    } else {
      head = fmt(t.barDone, vars);
      cardText = fmt(t.cardReady, vars);
      var vs = availableVariants();
      var sel = '';
      if (vs.length > 1) {
        sel = '<select class="arlo-gift__select" data-arlo-gift-variant aria-label="Choose your gift">' +
          vs.map(function (v) { return '<option value="' + v.id + '">' + esc(v.title) + '</option>'; }).join('') + '</select>';
      }
      actions = '<div class="arlo-gift__actions">' + sel +
        '<button type="button" class="arlo-gift__btn" data-arlo-gift-claim>' + esc(t.button || 'Claim my free gift') + '</button></div>';
    }
    var img = C.product.image ? '<div class="arlo-gift__img"><img src="' + esc(C.product.image) + '" alt="" loading="lazy"></div>' : '';
    return '<div class="arlo-gift__head">' + head + '</div>' +
      '<div class="arlo-gift__bar"><div class="arlo-gift__track"><span class="arlo-gift__fill" style="width:' + pct + '%"></span></div>' +
      '<div class="arlo-gift__goal">' + GIFT_SVG + '<span>' + money(C.thresholdCents, true) + '</span></div></div>' +
      (t.goalLabel ? '<div class="arlo-gift__goal-label">' + fmt(t.goalLabel, vars) + '</div>' : '<div style="height:5px"></div>') +
      '<div class="arlo-gift__card">' + img + '<div class="arlo-gift__body">' + cardText + actions + '</div></div>';
  }

  function draw(cart, s) {
    var main = document.querySelector('.quick-cart__main-content');
    var el = document.querySelector('.arlo-gift');
    if (!s.on || !main || !(cart.items || []).length) {
      if (el && el.parentElement) el.parentElement.removeChild(el);
      return;
    }
    injectStyles();
    var html = blockHtml(cart, s);
    if (!el || el.parentElement !== main) {
      if (el && el.parentElement) el.parentElement.removeChild(el);
      el = document.createElement('div');
      el.className = 'arlo-gift';
      main.insertBefore(el, main.firstChild);
    }
    var cls = 'arlo-gift' + (s.eligible ? ' arlo-gift--met' : '');
    if (el.className !== cls) el.className = cls;
    // Only write when something changed — keeps the drawer's own
    // MutationObservers (quick-cart.liquid, custom.js) quiet.
    if (el.__html !== html) {
      el.__html = html;
      var keep = el.querySelector('[data-arlo-gift-variant]');
      var keepVal = keep && keep.value;
      el.innerHTML = html;
      var sel = el.querySelector('[data-arlo-gift-variant]');
      if (sel && keepVal) sel.value = keepVal;
    }
  }

  if (PREVIEW) {
    // cfg = same shape as window.ARLO_FREE_GIFT; s = { total, eligible, hasGift }
    window.ArloFreeGiftView = function (cfg, s) {
      var saved = C; C = cfg;
      try { return { css: cssText(), html: blockHtml(null, s), met: !!s.eligible }; }
      finally { C = saved; }
    };
    return;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-arlo-gift-claim]');
    if (!btn) return;
    e.preventDefault();
    if (!lastCart || !active() || spend(lastCart) < C.thresholdCents) return;
    var box = btn.closest('.arlo-gift');
    var sel = box && box.querySelector('[data-arlo-gift-variant]');
    var vid = sel ? sel.value : (availableVariants()[0] || {}).id;
    if (!vid) return;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    addGift(vid);
  });

  // ── triggers ───────────────────────────────────────────────────────────────
  var timer = null;
  function refresh() {
    clearTimeout(timer);
    timer = setTimeout(function () {
      fetch('/cart.js?t=' + now(), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(evaluate)
        .catch(function () {});
    }, 150);
  }

  ['cart:updated', 'quick-cart:updated', 'cart:item-added', 'cart:item-removed',
   'cart:change', 'cart:update', 'arlo:cart-fresh'].forEach(function (evt) {
    document.addEventListener(evt, refresh);
  });

  // The drawer re-renders its rows after every change (add, qty, remove), and
  // swaps its whole content when going empty ↔ items. Ignore our own writes and
  // the other self-updating bits (totals, savings, countdown clock).
  var IGNORE = '.arlo-gift, .arlo-dual-checkout, #arlo-subtotal-price, .arlo-total-savings, .arlo-countdown, .arlo-countdown-note';
  function watch() {
    var root = document.querySelector('.quick-cart__container') || document.querySelector('.quick-cart');
    if (!root || !window.MutationObserver) return;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i], tg = m.target;
        if (tg && tg.nodeType !== 1) tg = tg.parentElement;
        if (!tg || !tg.closest) { refresh(); return; }
        if (tg.closest(IGNORE)) continue;
        var nodes = [].slice.call(m.addedNodes).concat([].slice.call(m.removedNodes));
        var onlyOurs = nodes.length && nodes.every(function (n) {
          return n.nodeType === 1 && n.classList && n.classList.contains('arlo-gift');
        });
        if (onlyOurs) continue;
        refresh(); return;
      }
    }).observe(root, { childList: true, subtree: true });
  }

  // Start/end times: a page left open across them updates on its own.
  var wasActive = active();
  setInterval(function () {
    var a = active();
    if (a !== wasActive) { wasActive = a; refresh(); }
  }, 30000);

  function start() { watch(); refresh(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
})();
